import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'
import { Button, LoadingButton } from '@olxbr/ds-core'

import BaseForm from '../BaseForm'
import EmailChip from '../../atoms/EmailChip'
import Form from '../../atoms/Form'
import Subtitle from '../../atoms/Subtitle'
import { isValidEmail } from '../../../util/validators'

import ErrorMsg from '../../atoms/ErrorMsg'
import Recaptcha from '../../atoms/Recaptcha'
import MailCheckInput from '../../organisms/MailCheckInput'
import Col from '../../atoms/Col'
import Row from '../../atoms/Row'

const Content = styled(View)`
  display: flex;
  flex-direction: column;
`

const SubtitleWrapper = styled(Subtitle)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const PageForm = styled(Form)`
  margin-top: 8px;
`

const EmailChipWrapper = styled(View)`
  display: flex;
  justify-content: center;
  margin-top: 8px;
  margin-bottom: 32px;
`

const RecaptchaWrapper = styled(View)`
  margin-top: 24px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const CaptchaErrorMsgView = styled(View)`
  display: flex;
  width: 90%;
`

const RowWrapper = styled(Row)`
  margin-bottom: 10px;
`

const recaptchaSiteKey = '6LfPClEUAAAAAD_O4ppsMM0r5EX5pcV5PH12rBIn'

const defaultEmailErrorMsg = 'Por favor, insira um endereço de e-mail válido.'
const getErrorMessage = email => {
  if (email) {
    return defaultEmailErrorMsg
  }
  return 'Este campo é obrigatório'
}

class EmailFormBaseTemplate extends BaseForm {
  constructor(props) {
    super(props)
    const { email = '', editable } = props

    let valid = false
    if (!editable) {
      valid = true
    } else {
      valid = email ? isValidEmail(email) : false
    }

    this.state = {
      ...this.state,
      validators: {
        email: {
          action: editable ? isValidEmail : () => true,
          message: getErrorMessage,
        },
        recaptchaToken: {
          action: value => !!value,
          message: 'Captcha não preenchido.',
        },
      },
      fields: {
        email: {
          value: email,
          pristine: !email,
          valid,
        },
        recaptchaToken: {
          value: '',
          pristine: true,
          valid: false,
        },
      },
      recaptchaReady: false,
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { fields } = state
    const { hasError, fieldError } = props
    if (hasError) {
      return {
        fields: {
          ...fields,
          email: {
            value: fields.email.value,
            msg: fieldError,
            valid: false,
            pristine: false,
          },
        },
      }
    }
    return null
  }

  renderTitle() {
    const { renderTitle } = this.props
    if (typeof renderTitle === 'string') {
      return <SubtitleWrapper>{renderTitle}</SubtitleWrapper>
    }
    return renderTitle()
  }

  onEmailChange = event => {
    const { value } = event.target
    this.onFieldChange(event, 'email', isValidEmail, getErrorMessage(value), true)
  }

  onEmailBlur = event => {
    const { value } = event.target
    this.onFieldChange(event, 'email', isValidEmail, getErrorMessage(value), false)
  }

  onMailCheckSuggestionClick = suggestion => {
    const { lurker } = this.props
    if (typeof lurker === 'object') {
      lurker.viewMessageEvt('mailcheck_suggestion_accepted', {
        mailcheck_original: this.state.fields.email.value,
        mailcheck_suggestion: suggestion,
      })
    }
    const valid = isValidEmail(suggestion)
    this.updateField('email', suggestion, valid, true)
  }

  renderEmail() {
    const { editable, onSwitchEmail } = this.props
    const { fields, isSubmitting } = this.state
    const { email } = fields
    const emailHasError = !email.valid && !email.pristine

    if (!editable) {
      return (
        <EmailChipWrapper>
          <EmailChip href="#" onClick={onSwitchEmail}>
            {email.value || this.props.email}
          </EmailChip>
        </EmailChipWrapper>
      )
    }

    return (
      <RowWrapper>
        <Col size="100">
          <MailCheckInput
            label="E-mail"
            value={email.value}
            onChange={event => this.onEmailChange(event)}
            onBlur={event => this.onEmailBlur(event)}
            error={email.msg}
            hasError={emailHasError}
            inputRef={x => {
              this.inputs.email = x
            }}
            disabled={isSubmitting}
            onSuggestionClick={this.onMailCheckSuggestionClick}
          />
        </Col>
      </RowWrapper>
    )
  }

  onRecaptchaLoad = () => this.setState({ recaptchaReady: true })

  onRecaptchaVerify = recaptchaToken => {
    if (recaptchaToken) {
      this.updateField('recaptchaToken', recaptchaToken, true)
    } else {
      const { lurker } = this.props

      this.setState({ isSubmitting: false })
      this.recaptcha.reset()
      lurker.viewMessageEvt('error', {
        accounts_reason: 'recaptcha_failed_token',
      })
      this.showError('showGenericError')
    }
  }

  onRecaptchaError = e => {
    const { lurker } = this.props

    lurker.viewMessageEvt('error', {
      accounts_reason: 'recaptcha_error',
      accounts_error_message: (e && e.message) || 'unknown',
    })
  }

  onRecaptchaExpiration = () => this.updateField('recaptchaToken', '', false)

  renderRecaptcha() {
    const { recaptchaToken } = this.state.fields
    const captchaHasError = !recaptchaToken.valid && !recaptchaToken.pristine

    const { useRecaptcha } = this.props
    if (useRecaptcha) {
      return (
        <RecaptchaWrapper>
          <React.Fragment>
            <Recaptcha
              ref={e => {
                this.recaptcha = e
              }}
              sitekey={recaptchaSiteKey}
              onLoad={this.onRecaptchaLoad}
              onVerify={this.onRecaptchaVerify}
              onError={this.onRecaptchaError}
              onExpire={this.onRecaptchaExpiration}
            />
            {captchaHasError && (
              <CaptchaErrorMsgView>
                <ErrorMsg>Captcha não preenchido.</ErrorMsg>
              </CaptchaErrorMsgView>
            )}
          </React.Fragment>
        </RecaptchaWrapper>
      )
    }
    return null
  }

  handleSubmit = evento => {
    const { lurker, onSubmit, submitEventName, useRecaptcha } = this.props
    const email = this.state.fields.email.value
    const recaptchaToken = this.state.fields.recaptchaToken?.value

    evento.preventDefault()
    this.setState({ isSubmitting: true })
    lurker.clickEvt(submitEventName)

    if (useRecaptcha) {
      this.validateBeforeSend().then(() => {
        this.checkFormIsValid()
          .then(() => {
            onSubmit(email, recaptchaToken)
              .then(() => this.setState({ isSubmitting: false }))
              .catch(() => {
                this.setState({ isSubmitting: false })
              })
          })
          .catch(() => {
            if (typeof lurker === 'object') {
              lurker.viewMessageEvt('error', {
                accounts_reason: 'invalid_fields',
                ...this.getInvalidFields(),
              })
            }
            this.setState({ isSubmitting: false })
          })
      })
    } else {
      this.validateSomeFieldsBeforeSend(['email'])
        .then(() => this.setState({ isSubmitting: false }), onSubmit(email))
        .catch(() => {
          this.setState({ isSubmitting: false })
          if (typeof lurker === 'object') {
            lurker.viewMessageEvt('error', {
              accounts_reason: 'invalid_fields',
              ...this.getInvalidFields(),
            })
          }
        })
    }
  }

  render() {
    const { isSubmitting } = this.state
    const { renderError, buttonText, errorPosition, isLocked } = this.props
    return (
      <Content>
        {/* Change the position of the error with the errorPosition property */}
        {errorPosition === 'top' ? renderError() : this.renderTitle()}

        {errorPosition === 'top' ? this.renderTitle() : renderError()}

        <PageForm method="POST" onSubmit={this.handleSubmit} noValidate>
          {this.renderEmail()}
          {this.renderRecaptcha()}

          <LoadingButton
            className="w-full"
            size="medium"
            disabled={isSubmitting || isLocked}
            isLoading={isSubmitting}
            onClick={this.handleSubmit}
          >
            {buttonText}
          </LoadingButton>
        </PageForm>
      </Content>
    )
  }
}

EmailFormBaseTemplate.defaultProps = {
  buttonText: 'Enviar',
  editable: true,
  isLocked: false,
  submitEventName: 'submit',
  useRecaptcha: true,
}

EmailFormBaseTemplate.propTypes = {
  buttonText: PropTypes.string,
  editable: PropTypes.bool,
  email: PropTypes.string,
  errorPosition: PropTypes.string,
  fieldError: PropTypes.string,
  hasError: PropTypes.bool,
  isLocked: PropTypes.bool,
  lurker: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onSubmit: PropTypes.func,
  onSwitchEmail: PropTypes.func,
  renderError: PropTypes.func,
  renderTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  submitEventName: PropTypes.string,
  useRecaptcha: PropTypes.bool,
}

export default EmailFormBaseTemplate
